import { FC, ReactNode, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  Chip,
  FormControl,
  FormControlProps,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete/Autocomplete';

import { ReactComponent as ChevronDownIcon } from '@/assets/icons/chevron-down-icon.svg';
import { ReactComponent as DeleteIcon } from '@/assets/icons/delete-icon2.svg';
import { ReactComponent as HorizontalDotsIcon } from '@/assets/icons/horizontal-dots-icon.svg';
import { camelToSnakeCase } from '@/shared/lib/strings';
import {
  COMPACT_FIELD_WIDTH,
  COMPACT_FORM_LABEL_SX,
  buildCompactInputSx,
} from '@/shared/ui/form-fields/fieldStyles';
import { FormError, FormLabel } from '@/shared/ui/styled';

import { InfoTooltip } from './RhfAutocomplete.styles';

type Props = {
  name: string;
  label?: string;
  isRequired?: boolean;
  startAdornment?: ReactNode;
  multiple?: boolean;
  freeSolo?: boolean;
  disablePortal?: boolean;
  options: { value: any; label: string; disabled?: boolean }[];
  onValueChange?: (value: string) => void;
  onSearchChange?: (search: string) => void;
  tags?: number;
  tooltip?: string;
  placeholder?: string;
  valueType?: 'string' | 'number' | 'boolean';
} & FormControlProps;

const RhfCompactAutocomplete: FC<Props> = ({
  name,
  isRequired,
  multiple,
  freeSolo,
  disablePortal,
  label,
  startAdornment,
  disabled,
  onValueChange,
  onSearchChange,
  options,
  tags = 4,
  tooltip,
  ...muiProps
}) => {
  const { t } = useTranslation();

  const getLabels = useCallback(
    (values: string[]) => {
      return values
        ?.slice(tags)
        ?.map((value: string) => options?.find((o) => o?.value === value)?.label || '')
        ?.join(', ');
    },
    [options, tags],
  );

  const renderTags = useCallback(
    (values: string[], getTagProps: AutocompleteRenderGetTagProps) => {
      return (
        <>
          {values.map((value: string, index: number) => {
            if (index < tags) {
              return (
                <Chip
                  variant="outlined"
                  color="primary"
                  size="small"
                  label={freeSolo ? value : options?.find((o) => o.value === value)?.label || ''}
                  {...getTagProps({ index })}
                  key={index}
                  deleteIcon={<DeleteIcon />}
                  sx={{
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    lineHeight: '0.75rem',
                    fontWeight: 600,
                    padding: 0,
                    height: '1.4rem',
                  }}
                />
              );
            } else {
              return null;
            }
          })}

          {values.length > tags && (
            <>
              <HorizontalDotsIcon style={{ margin: '0 0.25rem 0 0.1rem' }} />
              <Tooltip title={getLabels(values)}>
                <Chip
                  variant="outlined"
                  color="primary"
                  label={values.length - tags}
                  key={values.length + 1}
                  sx={{
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    lineHeight: '0.75rem',
                    fontWeight: 600,
                    padding: 0,
                    height: '1.4rem',
                  }}
                />
              </Tooltip>
            </>
          )}
        </>
      );
    },
    [getLabels, options, tags, freeSolo],
  );

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error }, formState: { isSubmitting } }) => {
        return (
          <FormControl
            error={!!error}
            {...muiProps}
            sx={{
              width: COMPACT_FIELD_WIDTH,
              ...buildCompactInputSx(!!error, {
                ...muiProps.sx,
                '& .MuiAutocomplete-clearIndicator': { marginRight: '4px' },
                '& .MuiAutocomplete-popupIndicator': { padding: 0, marginRight: '8px' },
                '& .MuiAutocomplete-popupIndicator svg': { width: '16px', height: '16px' },
              }),
            }}
          >
            {!!label && (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <FormLabel sx={COMPACT_FORM_LABEL_SX}>{label}</FormLabel>
                {!!tooltip && <InfoTooltip title={tooltip} />}
              </Stack>
            )}

            <Autocomplete
              {...field}
              value={multiple ? field.value || [] : field.value || null}
              autoComplete={false}
              ListboxProps={{ id: 'suggestions-list' }}
              onChange={(e, option, reason) => {
                let newValue;

                if (reason === 'clear') {
                  if (multiple) {
                    newValue = [];
                  } else {
                    newValue = '';
                  }
                } else {
                  if (multiple) {
                    const valuesArray =
                      option?.map((item: { value: any; label: string }) =>
                        item?.value === undefined ? item : item?.value,
                      ) || [];
                    newValue = valuesArray?.filter((item: any) => {
                      const firstIndex = valuesArray.indexOf(item);
                      const lastIndex = valuesArray.lastIndexOf(item);
                      return firstIndex === lastIndex;
                    });
                  } else if (freeSolo) {
                    newValue = option;
                  } else {
                    newValue = option?.value;
                  }
                }

                field.onChange(newValue);

                if (onValueChange) {
                  onValueChange(newValue);
                }
              }}
              onInputChange={
                onSearchChange &&
                ((_, search, reason) => {
                  if (reason !== 'reset') onSearchChange(search);
                })
              }
              filterOptions={onSearchChange && ((searchedOptions) => searchedOptions)}
              popupIcon={<ChevronDownIcon />}
              multiple={multiple || false}
              disabled={isSubmitting || disabled}
              disablePortal={disablePortal}
              freeSolo={freeSolo}
              // @ts-ignore
              options={options.slice().sort((a, b) => a.label.localeCompare(b.label))}
              // @ts-ignore
              getOptionLabel={(option) => option?.label ?? ''}
              // @ts-ignore
              isOptionEqualToValue={(option, value) => {
                return (
                  option.value ===
                  (typeof value === 'object' && value !== null ? value?.value : value)
                );
              }}
              disableCloseOnSelect={!!multiple}
              getOptionDisabled={(option) => option?.disabled}
              clearIcon={<DeleteIcon />}
              getOptionKey={(option) => option.value}
              renderTags={(_, getTagProps) => renderTags(field.value, getTagProps)}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(error)}
                    onFocus={undefined}
                    sx={{
                      paddingTop: 0,
                      paddingLeft: 0,
                      paddingBottom: 0,
                      cursor: 'pointer !important',
                    }}
                    placeholder={
                      multiple && field?.value?.length
                        ? ''
                        : muiProps.placeholder
                          ? muiProps.placeholder
                          : isRequired
                            ? t('required')
                            : t('optional')
                    }
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password',
                      id: `${camelToSnakeCase(name ?? 'no-label')}-input`,
                      value:
                        params.inputProps?.value ||
                        (multiple
                          ? []
                          : (options?.find((o) => o.value === field.value)?.label ?? '')),
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {startAdornment}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                const selected = multiple
                  ? field?.value?.includes(option?.value)
                  : field?.value === option?.value;
                return (
                  <li {...props} aria-selected={selected} key={props.key}>
                    <div className="list-item">{option.label}</div>
                  </li>
                );
              }}
            />

            {!!error && (
              <FormError>
                {error?.type === 'invalid_type' ? t('errors.required_field') : error.message}
              </FormError>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default RhfCompactAutocomplete;
